/**
 * SiteContext.tsx — holds the list of sites the CMS manages and which one
 * is currently selected. Every page that used to hardcode
 * `const SITE_ID = "demandhub"` now reads `useSite().currentSiteId`
 * instead, so switching sites in the sidebar (see components/SiteSwitcher)
 * immediately re-scopes every list/query on the page.
 *
 * The selection is persisted to localStorage (`cms.currentSiteId`) so a
 * refresh doesn't silently bounce someone back to a different site. If the
 * stored id no longer exists (site was deleted, or this is a fresh
 * browser), we fall back to the first site returned by GET /sites.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Site } from "@demandhub-cms/shared";
import { sitesApi } from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "cms.currentSiteId";

interface SiteContextValue {
  sites: Site[];
  currentSite: Site | undefined;
  currentSiteId: string;
  setCurrentSiteId: (id: string) => void;
  isLoading: boolean;
}

const SiteContext = createContext<SiteContextValue | null>(null);

export function SiteProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
    enabled: !!user, // sites is an authenticated-only endpoint
  });

  const [currentSiteId, setCurrentSiteIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });

  // Real functional fix (react-hooks/exhaustive-deps): `sitesQuery.data ??
  // []` used to build a brand-new `[]` on every render whenever data was
  // still undefined (loading), so `sites` had a different reference each
  // time even though nothing had actually changed — silently defeating the
  // `useEffect` and `useMemo` below that depend on it (both re-ran on
  // every render while loading instead of only when the real site list
  // changed). Memoizing the fallback keeps `sites` referentially stable
  // whenever `sitesQuery.data` itself hasn't changed.
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  // Once the site list loads, make sure the selection actually exists;
  // otherwise fall back to the first site. This also picks an initial
  // selection on a browser that's never chosen one before.
  //
  // Guarded on `!sitesQuery.isFetching`: right after creating a new site
  // (see SitesSettings.tsx) we deliberately switch to it before the list
  // refetch that includes it has resolved. Without this guard, this
  // effect would see the *stale* cached list (missing the brand-new
  // site), decide the selection doesn't exist, and silently bounce back
  // to the first site — a real race that was caught by screenshot
  // verification (creating a site never actually switched to it).
  useEffect(() => {
    if (sites.length === 0 || sitesQuery.isFetching) return;
    if (!sites.some((s) => s.id === currentSiteId)) {
      setCurrentSiteIdState(sites[0].id);
    }
  }, [sites, currentSiteId, sitesQuery.isFetching]);

  function setCurrentSiteId(id: string) {
    setCurrentSiteIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // ignore (private browsing / storage disabled) — selection just won't persist
    }
    // No manual query invalidation needed: every query that's scoped to a
    // site includes `currentSiteId` in its queryKey (see the pages under
    // src/pages/), so changing this state alone makes React Query treat
    // them as different queries and (re)fetch automatically. A blanket
    // `invalidateQueries()` here used to also force-refetch the `sites`
    // list itself and raced with this exact state update — see the guard
    // above.
  }

  const currentSite = useMemo(() => sites.find((s) => s.id === currentSiteId), [sites, currentSiteId]);

  return (
    <SiteContext.Provider
      value={{ sites, currentSite, currentSiteId, setCurrentSiteId, isLoading: sitesQuery.isLoading }}
    >
      {children}
    </SiteContext.Provider>
  );
}

export function useSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used within SiteProvider");
  return ctx;
}
